import { createSlice } from "@reduxjs/toolkit";

export const RESTAURANT_STORAGE_KEY = "pos:restaurant-state";

export const ITEM_STATUS = {
  DRAFT: "draft",
  PREPARING: "preparing",
  IN_PROGRESS: "in-progress",
  READY: "ready",
  SERVING: "serving",
  SERVED: "served",
};

export const TABLE_STATUS = {
  AVAILABLE: "available",
  OCCUPIED: "occupied",
  BILLING: "billing",
};

export const ONLINE_PLATFORMS = [
  "Talabat",
  "Zomato",
  "Careem",
  "Deliveroo",
  "Noon Food",
  "Other",
];

export const ONLINE_ZONE = "Online / Delivery";

const buildInitialState = () => ({
  zones: [],
  tables: [],
  orders: {},
  selectedTableId: null,
  invoiceCounter: 7001,
});

const loadState = () => {
  if (typeof window === "undefined") {
    return buildInitialState();
  }

  try {
    const raw = localStorage.getItem(RESTAURANT_STORAGE_KEY);

    if (!raw) return buildInitialState();

    return {
      ...buildInitialState(),
      ...JSON.parse(raw),
    };
  } catch {
    return buildInitialState();
  }
};

const persist = (state) => {
  if (typeof window === "undefined") return;

  try {
    const { selectedTableId, ...shared } = state;

    localStorage.setItem(RESTAURANT_STORAGE_KEY, JSON.stringify(shared));
  } catch {}
};

const ensureOrder = (state, tableId) => {
  if (!state.orders[tableId]) {
    state.orders[tableId] = {
      tableId,
      items: [],
      createdAt: new Date().toISOString(),
      invoiceNo: `RCDM${state.invoiceCounter}`,
    };

    state.invoiceCounter++;
  }

  return state.orders[tableId];
};

const lineKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// The table list API used to return the table types array directly; it now
// returns { tableTypes, phoneOrders, onlineOrders }. Accept both so a stale
// backend still renders the floor.
export const normalizeTablePayload = (payload) => {
  if (Array.isArray(payload)) {
    return { tableTypes: payload, phoneOrders: [], onlineOrders: [] };
  }

  const source = payload && typeof payload === "object" ? payload : {};
  const tableTypes = Array.isArray(source.tableTypes)
    ? source.tableTypes
    : Array.isArray(source.list)
      ? source.list
      : [];

  return {
    tableTypes,
    phoneOrders: Array.isArray(source.phoneOrders) ? source.phoneOrders : [],
    onlineOrders: Array.isArray(source.onlineOrders) ? source.onlineOrders : [],
  };
};

export const phoneOrderTableId = (orderHeadId) => `phone-${orderHeadId}`;
export const onlineOrderTableId = (orderHeadId) => `online-${orderHeadId}`;

// Shared shape for a server-reported phone / online order card. The order
// head id doubles as the cart id, so the details panel and menu work exactly
// as they do for a real table with an open order.
const buildVirtualTable = (
  state,
  existingTables,
  id,
  orderHeadId,
  fields,
  order = {},
) => {
  const existing = existingTables.find((t) => t.id === id);
  const localOrder = state.orders?.[id];
  const isPaid = Boolean(localOrder?.paidAt);

  return {
    id,
    seats: null,
    zone: ONLINE_ZONE,
    isOnline: true,
    available: false,
    activeOrderHeadId: String(orderHeadId),
    orderHeadId: String(orderHeadId),
    // Present only if the API reports the summary for phone/online orders too;
    // the floor plan falls back to local item state when it does not.
    itemCounts: order.itemCounts || null,
    oldestPendingAt: order.oldestPendingAt || null,
    allServedAt: order.allServedAt || null,
    serverPaidAt: order.paidAt || null,
    status:
      isPaid || existing?.status === TABLE_STATUS.BILLING
        ? TABLE_STATUS.BILLING
        : TABLE_STATUS.OCCUPIED,
    ...fields,
  };
};

const buildPhoneOrderTable = (state, existingTables, order) => {
  const orderHeadId = order?.orderHeadId;
  if (orderHeadId == null) return null;

  const customerName = [order.custFirstName, order.custLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const phone = String(order.mobileDel || "").trim();
  const id = phoneOrderTableId(orderHeadId);

  return buildVirtualTable(state, existingTables, id, orderHeadId, {
    name: customerName
      ? `${customerName}${phone ? ` (${phone})` : ""}`
      : phone
        ? `Phone ${phone}`
        : `Phone order #${orderHeadId}`,
    isPhone: true,
    platform: "Phone",
    orderNo: phone || String(orderHeadId),
    customerName,
    phone,
    custId: order.custId ?? null,
    email: order.delEmail || "",
    orderDate: order.orderDate || null,
  }, order);
};

const buildOnlineOrderTable = (state, existingTables, order) => {
  const orderHeadId = order?.orderHeadId;
  if (orderHeadId == null) return null;

  const id = onlineOrderTableId(orderHeadId);
  const existing = existingTables.find((t) => t.id === id);
  // The API does not (yet) say which platform the order came from, so keep
  // whatever this terminal recorded when it was created.
  const platform = order.platform || existing?.platform || "Online";
  const orderNo = order.orderNo || existing?.orderNo || String(orderHeadId);

  return buildVirtualTable(state, existingTables, id, orderHeadId, {
    name: `${platform} #${orderNo}`,
    platform,
    orderNo: String(orderNo),
    orderDate: order.orderDate || null,
  }, order);
};

export const restaurantSlice = createSlice({
  name: "restaurant",

  initialState: loadState(),

  reducers: {
    setRestaurantTables: (state, action) => {
      const { tableTypes, phoneOrders, onlineOrders } = normalizeTablePayload(
        action.payload,
      );

      state.zones = tableTypes.map((type) => type.tableTypeName);

      const existingTables = state.tables || [];

      // Phone and online orders are now reported by the table list API, so
      // the floor shows the server's copy of each. The only local entries
      // kept are ones the server does not know about yet (created on this
      // terminal before their order head existed) so the card does not vanish
      // mid-order on the next refresh.
      const serverVirtualTables = [
        ...phoneOrders.map((order) =>
          buildPhoneOrderTable(state, existingTables, order),
        ),
        ...onlineOrders.map((order) =>
          buildOnlineOrderTable(state, existingTables, order),
        ),
      ].filter(Boolean);

      const serverOrderHeadIds = new Set(
        serverVirtualTables.map((t) => String(t.activeOrderHeadId)),
      );

      const onlineTables = existingTables.filter(
        (t) =>
          t.isOnline &&
          !t.activeOrderHeadId &&
          !serverOrderHeadIds.has(String(state.orders?.[t.id]?.orderHeadId)),
      );

      state.tables = tableTypes.flatMap((type) =>
        type.tables.map((table) => {
          const existing = existingTables.find((t) => t.id === table.tableId);

          // The locally tracked status is only worth keeping while the table
          // still has something going on. Blindly preferring it meant a table
          // left "occupied" in localStorage stayed occupied forever, even once
          // the backend reported it free again - the stale-table complaint on
          // the restaurant page.
          const localOrder = state.orders?.[table.tableId];

          // A settled bill does not close the order, so a paid-but-unclosed
          // table has to stay occupied here even once the backend reports it
          // free again - otherwise paying wipes the order off the floor.
          const isPaidAndOpen = Boolean(localOrder?.paidAt);

          const hasLiveOrder = Boolean(
            localOrder?.items?.length || table.activeOrderHeadId || isPaidAndOpen,
          );
          const keepLocalStatus =
            existing?.status && (hasLiveOrder || Boolean(existing.reservation));

          return {
            id: table.tableId,
            name: table.tableName,
            description: table.tableDescription || "",
            seats: table.numberOfSeats,
            zone: type.tableTypeName,
            available: isPaidAndOpen ? false : table.available,
            // The backend reports the order head that is currently open on the
            // table; the details panel is fetched with it. Once paid it may
            // stop reporting one, so the id captured at payment is the fallback.
            activeOrderHeadId:
              table.activeOrderHeadId ??
              (isPaidAndOpen
                ? existing?.activeOrderHeadId ?? localOrder?.orderHeadId ?? null
                : null),
            // Per-table order summary, scoped by the backend to the open order
            // head. The floor plan derives its card state from this alone.
            itemCounts: table.itemCounts || null,
            oldestPendingAt: table.oldestPendingAt || null,
            allServedAt: table.allServedAt || null,
            serverPaidAt: table.paidAt || null,
            ...(existing?.reservation
              ? { reservation: existing.reservation }
              : {}),
            status: keepLocalStatus
              ? existing.status
              : table.available
                ? TABLE_STATUS.AVAILABLE
                : TABLE_STATUS.OCCUPIED,
          };
        }),
      );

      state.tables.push(...serverVirtualTables, ...onlineTables);

      persist(state);
    },

    selectTable: (state, action) => {
      const tableId = action.payload;

      state.selectedTableId = tableId;

      const table = state.tables.find((t) => t.id === tableId);

      if (table && table.status === TABLE_STATUS.AVAILABLE) {
        table.status = TABLE_STATUS.OCCUPIED;
      }

      ensureOrder(state, tableId);

      persist(state);
    },

    clearSelectedTable: (state) => {
      state.selectedTableId = null;

      persist(state);
    },

    createOnlineOrder: (state, action) => {
      const { platform, orderNo } = action.payload || {};

      if (!platform || !orderNo) return;

      const id = `online-${String(platform)
        .toLowerCase()
        .replace(/\s+/g, "-")}-${String(orderNo).trim()}`;

      let table = state.tables.find((t) => t.id === id);

      if (!table) {
        table = {
          id,
          name: `${platform} #${String(orderNo).trim()}`,
          seats: null,
          zone: ONLINE_ZONE,
          isOnline: true,
          platform,
          orderNo: String(orderNo).trim(),
          status: TABLE_STATUS.OCCUPIED,
        };

        state.tables.push(table);
      }

      state.selectedTableId = id;

      persist(state);
    },

    createPhoneOrder: (state, action) => {
      const { customerName, phone, custId } = action.payload || {};

      if (!phone || !String(phone).trim()) return;

      const normalizedPhone = String(phone).trim();

      const id = `phone-${normalizedPhone.replace(/\s+/g, "")}`;

      let table = state.tables.find((t) => t.id === id);

      if (!table) {
        table = {
          id,
          name: customerName?.trim()
            ? `${customerName.trim()} (${normalizedPhone})`
            : `Phone ${normalizedPhone}`,
          seats: null,
          zone: ONLINE_ZONE,
          isOnline: true,
          isPhone: true,
          platform: "Phone",
          orderNo: normalizedPhone,
          customerName: customerName?.trim() || "",
          phone: normalizedPhone,
          custId: custId ?? null,
          status: TABLE_STATUS.OCCUPIED,
        };

        state.tables.push(table);
      } else if (custId != null) {
        table.custId = custId;
      }

      const order = ensureOrder(state, id);
      order.isOnline = true;
      order.isPhone = true;
      order.platform = "Phone";
      order.orderNo = normalizedPhone;
      order.customerName = customerName?.trim() || "";
      order.phone = normalizedPhone;
      order.custId = custId ?? null;

      state.selectedTableId = id;

      persist(state);
    },

    reserveTable: (state, action) => {
      const { tableId, date, time, guests, arrangements, customerName, phone } =
        action.payload || {};

      if (!tableId || !date || !time) return;

      const table = state.tables.find((t) => t.id === tableId);

      if (!table) return;

      table.reservation = {
        date,
        time,
        guests: guests ? Number(guests) : null,
        arrangements: arrangements?.trim() || "",
        customerName: customerName?.trim() || "",
        phone: phone?.trim() || "",
        createdAt: new Date().toISOString(),
      };

      persist(state);
    },

    cancelReservation: (state, action) => {
      const tableId = action.payload;

      const table = state.tables.find((t) => t.id === tableId);

      if (table) {
        delete table.reservation;
        persist(state);
      }
    },

    addItemToTable: (state, action) => {
      const { tableId, item } = action.payload;

      if (!tableId || !item) return;

      const order = ensureOrder(state, tableId);

      const table = state.tables.find((t) => t.id === tableId);

      if (table && table.status === TABLE_STATUS.AVAILABLE) {
        table.status = TABLE_STATUS.OCCUPIED;
      }

      const existing = order.items.find(
        (line) =>
          line.prodDefId === item.prodDefId &&
          line.status === ITEM_STATUS.DRAFT,
      );

      if (existing) {
        existing.qty += 1;
      } else {
        order.items.push({
          lineId: lineKey(),

          prodDefId: item.prodDefId,

          title: item.title,

          station: item.station || "General",

          price: Number(item.price) || 0,

          discount: Number(item.discount) || 0,

          image: item.image || "",

          qty: 1,

          status: ITEM_STATUS.DRAFT,

          addedAt: new Date().toISOString(),
        });
      }

      persist(state);
    },

    incrementLine: (state, action) => {
      const { tableId, lineId } = action.payload;

      const line = state.orders[tableId]?.items.find(
        (l) => l.lineId === lineId,
      );

      if (line && line.status === ITEM_STATUS.DRAFT) {
        line.qty++;

        persist(state);
      }
    },

    decrementLine: (state, action) => {
      const { tableId, lineId } = action.payload;

      const order = state.orders[tableId];

      if (!order) return;

      const line = order.items.find((l) => l.lineId === lineId);

      if (line && line.status === ITEM_STATUS.DRAFT) {
        line.qty--;

        if (line.qty <= 0) {
          order.items = order.items.filter((l) => l.lineId !== lineId);
        }

        persist(state);
      }
    },

    removeLine: (state, action) => {
      const { tableId, lineId } = action.payload;

      const order = state.orders[tableId];

      if (!order) return;

      order.items = order.items.filter((l) => l.lineId !== lineId);

      persist(state);
    },

    sendToKitchen: (state, action) => {
      const tableId = action.payload;

      const order = state.orders[tableId];

      if (!order) return;

      const kotNo = Date.now();

      // If the order already has items that were previously sent to the
      // kitchen, this new batch is an add-on and should be prioritized.
      const isAddon = order.items.some(
        (line) => line.status !== ITEM_STATUS.DRAFT,
      );

      order.items.forEach((line) => {
        if (line.status === ITEM_STATUS.DRAFT) {
          line.status = ITEM_STATUS.PREPARING;

          line.kotNo = kotNo;

          line.isAddon = isAddon;

          line.sentAt = new Date().toISOString();
        }
      });

      persist(state);
    },

    acceptLineInKitchen: (state, action) => {
      const { tableId, lineId } = action.payload;

      const line = state.orders[tableId]?.items.find(
        (l) => l.lineId === lineId,
      );

      if (line && line.status === ITEM_STATUS.PREPARING) {
        line.status = ITEM_STATUS.IN_PROGRESS;

        persist(state);
      }
    },

    markLineReady: (state, action) => {
      const { tableId, lineId } = action.payload;

      const line = state.orders[tableId]?.items.find(
        (l) => l.lineId === lineId,
      );

      if (
        line &&
        (line.status === ITEM_STATUS.PREPARING ||
          line.status === ITEM_STATUS.IN_PROGRESS)
      ) {
        line.status = ITEM_STATUS.READY;

        line.readyAt = new Date().toISOString();

        persist(state);
      }
    },

    markKotReady: (state, action) => {
      const { tableId, kotNo } = action.payload;

      const order = state.orders[tableId];

      if (!order) return;

      order.items.forEach((line) => {
        if (
          line.kotNo === kotNo &&
          (line.status === ITEM_STATUS.PREPARING ||
            line.status === ITEM_STATUS.IN_PROGRESS)
        ) {
          line.status = ITEM_STATUS.READY;

          line.readyAt = new Date().toISOString();
        }
      });

      persist(state);
    },

    acceptLineInServer: (state, action) => {
      const { tableId, lineId } = action.payload;

      const line = state.orders[tableId]?.items.find(
        (l) => l.lineId === lineId,
      );

      if (line && line.status === ITEM_STATUS.READY) {
        line.status = ITEM_STATUS.SERVING;

        persist(state);
      }
    },

    markLineServed: (state, action) => {
      const { tableId, lineId } = action.payload;

      const line = state.orders[tableId]?.items.find(
        (l) => l.lineId === lineId,
      );

      if (line) {
        line.status = ITEM_STATUS.SERVED;

        // The floor plan waits a while after serving before nagging to settle.
        line.servedAt = new Date().toISOString();

        persist(state);
      }
    },

    // Payment and closing the order are deliberately separate. The bill can be
    // settled at any point, but the order stays on the floor so the kitchen and
    // servers can finish; completeBill is what actually closes it afterwards.
    markOrderPaid: (state, action) => {
      const { tableId, paymentMethod, paidTotal, orderHeadId } =
        action.payload || {};

      if (!tableId) return;

      const order = ensureOrder(state, tableId);

      order.paidAt = new Date().toISOString();
      order.paymentMethod = paymentMethod || null;
      order.paidTotal = Number(paidTotal) || 0;

      if (orderHeadId) {
        order.orderHeadId = String(orderHeadId);
      }

      const table = state.tables.find((t) => t.id === tableId);

      if (table) {
        table.status = TABLE_STATUS.BILLING;
        table.available = false;
      }

      persist(state);
    },

    completeBill: (state, action) => {
      const tableId = action.payload;

      delete state.orders[tableId];

      const table = state.tables.find((t) => t.id === tableId);

      if (table) {
        if (table.isOnline) {
          state.tables = state.tables.filter((t) => t.id !== tableId);
        } else {
          table.status = TABLE_STATUS.AVAILABLE;
          // The order head is closed, so the details panel must not fall back
          // to it while waiting for the next table list refresh.
          table.activeOrderHeadId = null;
          table.available = true;
        }
      }

      persist(state);
    },

    discardOrder: (state, action) => {
      const tableId = action.payload;

      delete state.orders[tableId];

      const table = state.tables.find((t) => t.id === tableId);

      if (table) {
        if (table.isOnline) {
          state.tables = state.tables.filter((t) => t.id !== tableId);
        } else {
          table.status = TABLE_STATUS.AVAILABLE;
          table.activeOrderHeadId = null;
          table.available = true;
        }
      }

      if (state.selectedTableId === tableId) {
        state.selectedTableId = null;
      }

      persist(state);
    },

    hydrateFromStorage: (state, action) => {
      const next = action.payload || {};

      state.tables = next.tables || state.tables;

      state.zones = next.zones || state.zones;

      state.orders = next.orders || state.orders;

      state.invoiceCounter = next.invoiceCounter || state.invoiceCounter;
    },
  },

  extraReducers: (builder) => {
    // The floor plan, its open orders and the invoice counter are persisted to
    // localStorage, so without this a signed-out terminal kept showing the
    // previous session's occupied tables and orders after the next login.
    builder.addCase("auth/logout", () => {
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem(RESTAURANT_STORAGE_KEY);
        } catch {}
      }

      return buildInitialState();
    });
  },
});

export const {
  setRestaurantTables,
  selectTable,
  clearSelectedTable,
  createOnlineOrder,
  createPhoneOrder,
  reserveTable,
  cancelReservation,
  addItemToTable,
  markOrderPaid,
  incrementLine,
  decrementLine,
  removeLine,
  sendToKitchen,
  acceptLineInKitchen,
  markLineReady,
  markKotReady,
  acceptLineInServer,
  markLineServed,
  completeBill,
  discardOrder,
  hydrateFromStorage,
} = restaurantSlice.actions;

export const selectTables = (state) => state.restaurant.tables || [];

export const selectZones = (state) => state.restaurant.zones || [];

export const selectOrders = (state) => state.restaurant.orders;

export const selectSelectedTableId = (state) =>
  state.restaurant.selectedTableId;

export const selectOrderByTable = (tableId) => (state) =>
  state.restaurant.orders[tableId];

export const selectIsOrderPaid = (tableId) => (state) =>
  Boolean(state.restaurant.orders?.[tableId]?.paidAt);

export const selectActiveOrderHeadId = (tableId) => (state) =>
  state.restaurant.tables?.find((t) => t.id === tableId)?.activeOrderHeadId ??
  null;

export default restaurantSlice.reducer;
